import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { PopoverController } from '@ionic/angular';
// Models
import { User, Vehicle, GeneralPilotInfo, PilotLogbookBackend  } from '@mocca-technologies/model';

import { selectedValues } from '../../logbooks-menu/logbooks-menu.component';
import { LogbooksTimeFilter, TimeFilter } from '../../logbooks-time-filter/logbooks-time-filter.component';
// Highcharts
import * as Highcharts from 'highcharts';
// Services
import { LogbooksSharedService } from '../../../../../services/logbooks-shared.service';
import { CoreService } from 'src/app/services/core.service';
import { LogbookService } from 'src/app/services/logbooks.service';
// Utils
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-pilot-summary',
  templateUrl: './pilot-summary.component.html',
  styleUrls: ['./pilot-summary.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class PilotSummaryComponent implements OnInit {

  /**
   * Input variables
   */

  @Input() set pilotLogbook(value: PilotLogbookBackend) {
    this._pilotLogbook = value;

    this.updateCharts();
  };

  @Input() selectedValues: selectedValues;

  @Input() vehicleList: Vehicle[] = [];

  /**
   * Output variables
   */

  @Output() timeFilterChange = new EventEmitter<TimeFilter>();
  @Output() selectedVehicleFromGraph = new EventEmitter<string>();

  /**
   * CHildren Variables
   */

  @ViewChild(LogbooksTimeFilter) logbooksTimeFilter: LogbooksTimeFilter;

  /**
   * Variables
   */

  selectedPilot: User = null;

  selectedPilotGeneralInfo: GeneralPilotInfo = null;

  dronesChart: Highcharts.Chart = null;

  private _pilotLogbook: PilotLogbookBackend;

  // #region --> Getters and Setters

  get pilotLogbook(): PilotLogbookBackend {
    return this._pilotLogbook;
  }

  get user() {
      return JSON.parse(localStorage.getItem('user'));
  }

  // #endregion --> Getters and Setters

  constructor(
    public logbookService: LogbookService,
    public popoverController: PopoverController,
    private sharedService: LogbooksSharedService,
    private coreService: CoreService,
    private translateService: TranslateService
  ) {}

  /**
   * Gets the primary color from CSS variable
   */
  private getPrimaryColor(): string {
    const element = document.documentElement;
    const primaryColor = getComputedStyle(element).getPropertyValue('--ion-color-primary').trim();
    return primaryColor || '#0066FF';  // Fallback color
  }

  /**
   * Generates a color palette derived from primary color
   */
  private generateColorPalette(): string[] {
    const primaryColor = this.getPrimaryColor();
    const hslColor = this.hexToHsl(primaryColor);
    const palette: string[] = [];

    // Generate variations of the primary color
    for (let i = 0; i < 10; i++) {
      const lightness = 30 + (i * 5); // Vary lightness from 30 to 75
      palette.push(`hsl(${hslColor.h}, ${hslColor.s}%, ${lightness}%)`);
    }
    return palette;
  }

  /**
   * Converts hex color to HSL
   */
  private hexToHsl(hex: string): { h: number; s: number; l: number } {
    hex = hex.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  }

  // #region --> Life Cycle Methods

  ngOnInit() {
    this.translateService.onLangChange.subscribe(() => {
      if (this.dronesChart) {
        this.updateChartTitles();
      }
    });
  }

  // #endregion --> Life Cycle Methods

  // #region --> Highchart Methods

  /**
   * Initializes the Highchart charts
   */

  initializeCharts() {

    this.dronesChart = Highcharts.chart('dronesChartContainer', {
      colors: this.generateColorPalette(),
      chart: {
        type: 'pie'
      },
      title: {
        text: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.TOTAL_OPERATIONS_BY_DRONE'),
        style: { color: 'var(--ion-color-primary)', fontWeight: 'normal' }
      },
      series: [{
        type: 'pie',
        name: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.OPERATIONS'),
        data: this.pilotLogbook?.operationsPerDrone?.map(droneRegister =>
          ({
            name: droneRegister?.drone?.plate,
            y: droneRegister?.numOperations
          }))
      }],
      time: {
        useUTC: false
      },
      credits: {
        enabled: false
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          point: {
            events: {
              click: (event) => {
                this.selectVehicle(event.point.name);
              }
            }
          },
        }
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 100,
              maxHeight: 180
            },
            chartOptions: {
              chart: {
                spacing: [5, 5, 5, 50],
              },
              title: {
                align: 'left',
                x: 0,
                y: 15,
                margin: 0,
                style: { fontSize: '8px' }
              },
              legend: {
                itemStyle: { fontSize: '10px' },
                itemDistance: 2,
                layout: 'vertical',
                align: 'center',
                verticalAlign: 'bottom'
              },
              plotOptions: {
                pie: {
                  size: '95%',
                  dataLabels: {
                    enabled: false,
                  }
                }
              }
            }
          },
          // Small screens
          {
            condition: {
              minWidth: 100,
              minHeight: 180,
              maxWidth: 173,
              maxHeight: 200
            },
            chartOptions: {
              chart: {
                spacing: [5, 5, 5, 50],
              },
              title: {
                align: 'left',
                x: 0,
                y: 15,
                margin: 0,
                style: { fontSize: '10px' }
              },
              legend: {
                itemStyle: { fontSize: '12px' },
                itemDistance: 2,
                layout: 'vertical',
                align: 'center',
                verticalAlign: 'bottom'
              },
              plotOptions: {
                pie: {
                  size: '95%',
                  dataLabels: {
                    enabled: false,
                  }
                }
              }
            }
          },
          // Medium screens
          {
            condition: {
              minWidth: 163,
              minHeight: 181,
            },
            chartOptions: {
              chart: {
                spacing: [5, 5, 5, 5]
              },
              title: {
                style: { fontSize: '14px' }
              },
              legend: {
                itemStyle: { fontSize: '14px' },
                itemDistance: 2
              },
              plotOptions: {
                pie: {
                  size: '95%',
                  dataLabels: {
                    style: { fontSize: '10px' },
                    distance: 2
                  }
                }
              }
            }
          },
          // Large screens
          {
            condition: {
              minWidth: 220,
              minHeight: 198
            },
            chartOptions: {
              chart: {
                spacing: [5, 5, 5, 5]
              },
              title: {
                style: { fontSize: '16px' }
              },
              legend: {
                itemStyle: { fontSize: '16px' },
                itemDistance: 2
              },
              plotOptions: {
                pie: {
                  size: '95%',
                  dataLabels: {
                    style: { fontSize: '12px' },
                    distance: 2
                  }
                }
              }
            }
          }
        ]
      }
    });
  }

  /**
   * Updates the Highchart charts data
   */

  updateCharts() {

    if (this.summaryData()) {
      if (this.dronesChart) {
        this.dronesChart.update({
          xAxis: {
            categories: this.pilotLogbook?.operationsPerDrone.map(droneRegister => droneRegister?.drone?.plate)
          },
          series: [{
            type: 'pie',
            name: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.OPERATIONS'),
            data: this.pilotLogbook?.operationsPerDrone.map(droneRegister =>
              ({
                name: droneRegister?.drone?.plate,
                y: droneRegister?.numOperations
              }))
          }]
        });

        //this.getHighchartsSvg();
      } else {
        this.initializeCharts();
        //this.getHighchartsSvg();
      }
    }
  }

  /**
   * Gets the SVG of the Highchart chart
   */

  getHighchartsSvg() {

    if (this.dronesChart) {
      !environment.production && console.log('Getting SVG');
      const svg = (this.dronesChart as any).getSVG();
      !environment.production && console.log(svg);
      this.sharedService.setPilotsTotalOperationsByDroneSvg(svg);
    }
  }

  selectVehicle(plate: string) {

    if (plate && this.checkDroneBelongsToUser(plate)) {
      this.selectedVehicleFromGraph.emit(plate);
    } else {
      this.coreService.showServerResponse('400', this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.BAD_REQUEST'));
    }
  }

  checkDroneBelongsToUser(plate: string): boolean {
    let belongs = false;
    if (this.vehicleList && this.vehicleList.length > 0) {
      const vehicle = this.vehicleList.find(v => v.plate === plate);
      if (vehicle) {
        belongs = true;
      }
    }
    return belongs;
  }

  /**
   * Updates the chart titles when language changes
   */
  private updateChartTitles(): void {
    if (this.dronesChart) {
      this.dronesChart.update({
        title: {
          text: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.TOTAL_OPERATIONS_BY_DRONE')
        },
        series: [{
          name: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.OPERATIONS')
        }]
      } as any, false);
      this.dronesChart.redraw();
    }
  }

  // #endregion --> Highchart Methods

  // #region --> Time Filter Methods

  /**
   * Resets the time filter
   */

  resetTimeFilter() {
    this.logbooksTimeFilter.resetNumYears();
  }

  /**
   * Updates the time filter
   * @param timeFilter
   */

  updateTimeFilter(timeFilter: TimeFilter) {
    this.timeFilterChange.emit(timeFilter);
  }

  // #endregion --> Time Filter Methods

  // #region --> Page Methods

  /**
   * This methods checks if there is summary data
   * @returns
   */
  summaryData(): boolean {
    let summaryData = false;

    if ( this.pilotLogbook?.averageFlightTime > 0 && this.pilotLogbook?.totalFlightTime > 0) {
      summaryData = true;
    }

    return summaryData;

  }

  // #endregion --> Page Methods

}
